// User's local sample library — everything forged or imported on
// this device. Mirrors the Vault visually (search, scrollable card
// stack, single-action insert, audition) but the data source is
// the IDB-backed `SampleStoreContext` rather than a static catalog.
//
// One click on a row appends `const <var> = s("<sampleName>")` to
// the song. Audition runs the same `s("<sampleName>")` through the
// shared preview machinery — the sample is already registered with
// Strudel via rehydrate (or via the forge commit), so this is a pure
// playback surface, no decode-on-click.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useEditContext } from '../editContext';
import { useSampleStore } from './SampleStoreContext';
import { makeLocalSampleStatement } from './sampleStatement';
import type { Sample, SampleOrigin } from '../../samples/types';
import type { PreviewOps } from './SoundLibrary';

export type MySamplesProps = {
  preview: PreviewOps;
  /** Called by the empty-state's "Open Forge" affordance. The MINE
   *  section's parent owns the actual transition (in step 7 this
   *  flips MINE into Forge mode in-rail; today it opens the modal). */
  onOpenForge: () => void;
};

export function MySamples({ preview, onOpenForge }: MySamplesProps) {
  const ops = useEditContext();
  const { samples, loading, storage, refresh } = useSampleStore();
  const [query, setQuery] = useState('');
  const [flashId, setFlashId] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return samples;
    return samples.filter((s) => {
      if (s.name.toLowerCase().includes(q)) return true;
      if (s.tags.some((t) => t.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [samples, query]);

  const insert = (sample: Sample) => {
    let insertedId: string | null = null;
    ops.update((draft) => {
      const taken = new Set<string>();
      for (const s of draft.statements) {
        if (s.kind === 'const') taken.add(s.name);
      }
      const stmt = makeLocalSampleStatement({
        sampleName: sample.name,
        takenNames: taken,
      });
      draft.statements.push(stmt);
      insertedId = stmt.id;
    });
    if (insertedId) ops.revealBlock(insertedId);

    setFlashId(sample.id);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlashId(null), 550);
  };

  const handleDelete = async (sample: Sample) => {
    if (!storage) return;
    const ok = window.confirm(`Delete "${sample.name}"? This can't be undone.`);
    if (!ok) return;
    try {
      await storage.deleteSample(sample.id);
      refresh();
    } catch {
      // best-effort; refresh anyway so the UI re-pulls truth.
      refresh();
    }
  };

  if (samples.length === 0 && !loading) {
    return <EmptyState onOpenForge={onOpenForge} />;
  }

  return (
    <div className="relative w-full h-full bg-zinc-950 flex flex-col min-h-0">
      <Search value={query} onChange={setQuery} visible={filtered.length} total={samples.length} />

      <div
        aria-label="My samples"
        className="flex-1 min-h-0 overflow-y-auto px-3 pb-6 pt-1 space-y-1.5 scrollbar-thin"
      >
        {loading && samples.length === 0 ? (
          <div className="text-center text-[10px] uppercase tracking-[0.3em] text-zinc-600 py-8">
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <NoMatches query={query} />
        ) : (
          filtered.map((sample) => (
            <SampleRow
              key={sample.id}
              sample={sample}
              flashing={flashId === sample.id}
              previewing={preview.activeId === sample.id}
              canPreview={preview.canPlay}
              onInsert={() => insert(sample)}
              onPreviewToggle={() => {
                if (preview.activeId === sample.id) {
                  preview.stop();
                } else {
                  preview.start(sample.id, `s("${sample.name}")`, sample.name);
                }
              }}
              onDelete={() => handleDelete(sample)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Empty state ────────────────────────────────────────────────────

function EmptyState({ onOpenForge }: { onOpenForge: () => void }) {
  return (
    <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <div className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500">
        No samples yet
      </div>
      <p className="font-body text-[11px] leading-[1.6] text-zinc-500 max-w-[200px]">
        Record, import, synthesise, or chop a sample in the Forge — it'll appear here ready to drop into the song.
      </p>
      <button
        type="button"
        onClick={onOpenForge}
        className="mt-2 inline-flex items-center gap-2 bg-yellow-300 text-zinc-950 px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-semibold rounded-sm hover:brightness-110 transition-[filter]"
      >
        <span aria-hidden="true">⚒</span>
        Open Forge
      </button>
    </div>
  );
}

function NoMatches({ query }: { query: string }) {
  return (
    <div className="text-center text-[10px] uppercase tracking-[0.3em] text-zinc-600 py-8">
      No matches for "{query}"
    </div>
  );
}

// ─── Search ─────────────────────────────────────────────────────────

function Search({
  value,
  onChange,
  visible,
  total,
}: {
  value: string;
  onChange: (v: string) => void;
  visible: number;
  total: number;
}) {
  return (
    <div className="px-4 pt-3 pb-2">
      <label className="relative block">
        <span className="sr-only">Search my samples</span>
        <svg
          viewBox="0 0 16 16"
          className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600"
          stroke="currentColor"
          strokeWidth={1.5}
          fill="none"
        >
          <circle cx={7} cy={7} r={4.5} />
          <path d="M10.5 10.5 L14 14" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          inputMode="search"
          placeholder="search names, tags…"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-zinc-900/70 border border-zinc-800 focus:border-yellow-300/50 focus:bg-zinc-900 focus:outline-none rounded text-[11px] text-zinc-200 placeholder:text-zinc-600 placeholder:tracking-wide pl-8 pr-12 py-1.5 transition-colors font-body"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-zinc-600 tabular-nums tracking-wider">
          {visible}
          <span className="text-zinc-700">/{total}</span>
        </span>
      </label>
    </div>
  );
}

// ─── Sample row ─────────────────────────────────────────────────────

function SampleRow({
  sample,
  flashing,
  previewing,
  canPreview,
  onInsert,
  onPreviewToggle,
  onDelete,
}: {
  sample: Sample;
  flashing: boolean;
  previewing: boolean;
  canPreview: boolean;
  onInsert: () => void;
  onPreviewToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className={
        'group relative flex items-stretch border bg-zinc-900/50 rounded-sm overflow-hidden transition-all ' +
        (flashing
          ? 'border-yellow-300 shadow-[0_0_0_1px_rgba(253,224,71,0.6)]'
          : 'border-zinc-800 hover:border-yellow-300/30')
      }
    >
      <button
        type="button"
        onClick={onInsert}
        title={`Insert const ${sample.name} = s("${sample.name}")`}
        className="flex-1 min-w-0 flex items-center gap-2.5 px-2.5 py-2 text-left"
      >
        <OriginGlyph origin={sample.origin} />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-zinc-100 font-body truncate">
            {sample.name}
          </div>
          <div className="text-[9px] text-zinc-500 tracking-wider tabular-nums flex items-center gap-2">
            <span>{(sample.durationMs / 1000).toFixed(2)}s</span>
            {sample.tags.length > 0 && (
              <span className="truncate">· {sample.tags.slice(0, 3).join(', ')}</span>
            )}
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={onPreviewToggle}
        disabled={!canPreview}
        title={previewing ? 'Stop audition' : 'Audition'}
        aria-label={previewing ? 'Stop audition' : 'Audition'}
        className={
          'shrink-0 w-8 grid place-items-center text-zinc-500 hover:text-yellow-300 disabled:text-zinc-700 disabled:cursor-not-allowed border-l border-zinc-800 transition-colors ' +
          (previewing ? 'text-yellow-300' : '')
        }
      >
        {previewing ? <StopIcon /> : <PlayIcon />}
      </button>

      <button
        type="button"
        onClick={onDelete}
        title="Delete sample"
        aria-label={`Delete ${sample.name}`}
        className="shrink-0 w-7 grid place-items-center text-zinc-600 hover:text-rose-400 border-l border-zinc-800 transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
      >
        <TrashIcon />
      </button>
    </div>
  );
}

// Tiny silkscreen-style 3-letter origin tag. Doubles as a glyph; no
// extra SVG asset to maintain. Color encodes the verb family.
function OriginGlyph({ origin }: { origin: SampleOrigin }) {
  const meta = ORIGIN_META[origin.kind];
  return (
    <span
      aria-hidden="true"
      title={meta.label}
      className={
        'shrink-0 inline-grid place-items-center w-7 h-7 rounded-sm font-display text-[8px] tracking-[0.15em] uppercase font-semibold ' +
        meta.cls
      }
    >
      {meta.tag}
    </span>
  );
}

const ORIGIN_META: Record<
  SampleOrigin['kind'],
  { tag: string; label: string; cls: string }
> = {
  import: { tag: 'IMP', label: 'Imported', cls: 'bg-zinc-800 text-zinc-400' },
  record: { tag: 'REC', label: 'Recorded', cls: 'bg-rose-400/15 text-rose-300' },
  bounce: { tag: 'BNC', label: 'Bounced from a pattern', cls: 'bg-amber-400/15 text-amber-300' },
  synth: { tag: 'SYN', label: 'Synthesised', cls: 'bg-violet-300/15 text-violet-200' },
  chop: { tag: 'CHP', label: 'Chopped', cls: 'bg-emerald-300/15 text-emerald-200' },
  mangle: { tag: 'MNG', label: 'Mangled', cls: 'bg-fuchsia-300/15 text-fuchsia-200' },
  speech: { tag: 'SPK', label: 'Speech-synthesised', cls: 'bg-cyan-300/15 text-cyan-200' },
  wavetable: { tag: 'DRW', label: 'Drawn wavetable', cls: 'bg-yellow-300/15 text-yellow-300' },
  convolve: { tag: 'CNV', label: 'Convolved', cls: 'bg-sky-300/15 text-sky-200' },
  sidechain: { tag: 'SDC', label: 'Sidechain-ducked', cls: 'bg-orange-300/15 text-orange-200' },
  'stretch-to-tempo': { tag: 'STR', label: 'Stretched to tempo', cls: 'bg-lime-300/15 text-lime-200' },
  freeze: { tag: 'FRZ', label: 'Granular freeze', cls: 'bg-indigo-300/15 text-indigo-200' },
  'pitch-to-key': { tag: 'PTK', label: 'Pitch-shifted to key', cls: 'bg-teal-300/15 text-teal-200' },
};

function PlayIcon() {
  return (
    <svg viewBox="0 0 16 16" width={11} height={11} aria-hidden="true">
      <path d="M4 3 L13 8 L4 13 Z" fill="currentColor" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg viewBox="0 0 16 16" width={10} height={10} aria-hidden="true">
      <rect x={3} y={3} width={10} height={10} fill="currentColor" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={11}
      height={11}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 5 L13 5" />
      <path d="M5.5 5 V3.5 H10.5 V5" />
      <path d="M4.5 5 L5 13 H11 L11.5 5" />
    </svg>
  );
}
